import {
  AgentToolCallStatus,
  ToolName,
} from '@debbie/common/dist/agents/index.js'
import { CaseStatus, EndReasonHandle } from '@debbie/common/dist/cases/index.js'
import {
  CaseVoucherSource,
  State,
} from '@debbie/common/dist/economy/case-vouchers/index.js'
import { File as FileType } from '@debbie/common/dist/files/index.js'
import { Address, Currency, Entity } from '@debbie/common/dist/index.js'
import {
  PropertyData,
  PropertyDataType,
  filterPropertyTypes,
} from '@debbie/common/dist/properties/index.js'
import {
  aggregate,
  assertUnreachable,
  counted,
  objectBy,
  sortBy,
} from '@debbie/common/dist/util/index.js'
import { h } from '@debbie/cortex/dist/index.js'
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { useUpdateTypes } from '@debbie/cortex/dist/update-types/index.js'
import { Check, X } from '@debbie/iris/dist/components/Icons/index.js'
import {
  Badge,
  CasePreview,
  CaseStatus as CaseStatusComponent,
  File,
  Loading,
  Money,
  PlaceholderLoader,
  ResponsiblePreview,
  Timestamp,
} from '@debbie/iris/dist/components/index.js'
import THEME from '@debbie/iris/dist/theme.js'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

export const MoneyDisplay = (props: { value: number }) => (
  <Money amount={props.value}></Money>
)

export const DateDisplay = (props: { value: string }) => (
  <Timestamp date={props.value} />
)

export const StringDisplay = (props: { value: string }) => <>{props.value}</>

export const FloatDisplay = (props: { value: number }) => <>{props.value}</>

export const IntDisplay = (props: { value: number }) => <>{props.value}</>

export const UUIDDisplay = (props: { value: string }) => <>{props.value}</>

export const BooleanDisplay = (props: { value: boolean }) => {
  const lang = useSelector((s: RootState) => s.lang)

  return props.value ? <>{lang.misc.yes}</> : <>{lang.misc.no}</>
}

export const UnknownDisplay = (props: { value: unknown }) => {
  return <>{`${props.value as any}`}</>
}

export const JSONDisplay = (props: { value: unknown }) => (
  <code>{JSON.stringify(props.value, null, 2)}</code>
)

export const EnumDisplay = (props: { value: string }) => <>{props.value}</>

// Custom displays

export const CaseVoucherSourceDisplay = (props: {
  value: CaseVoucherSource
}) => {
  const lang = useSelector((s: RootState) => s.lang)
  return <>{lang.caseVoucherSources[props.value]}</>
}

export const CaseVoucherStateDisplay = (props: { value: State | null }) => {
  const lang = useSelector((s: RootState) => s.lang)
  return props.value ? (
    <Badge theme={THEME.COLOR_ACCENT}>
      {lang.caseVoucherStates[props.value]}
    </Badge>
  ) : null
}

export const CurrencyDisplay = (props: { value: Currency }) => {
  return <Badge theme={THEME.COLOR_ACCENT}>{props.value}</Badge>
}

// Aggregate displays

export const PropertyDisplay = (props: {
  property: {
    data: PropertyData
    type: PropertyDataType
  }
}) => {
  switch (props.property.type) {
    case PropertyDataType.singleLineText: {
      return (
        <StringDisplay value={props.property.data as string}></StringDisplay>
      )
    }
    case PropertyDataType.multiLineText: {
      return <code>{props.property.data as string}</code>
    }
    case PropertyDataType.number: {
      return <FloatDisplay value={props.property.data as number} />
    }
    case PropertyDataType.checkbox: {
      return <BooleanDisplay value={props.property.data as boolean} />
    }
    case PropertyDataType.datePicker: {
      return <DateDisplay value={props.property.data as string}></DateDisplay>
    }
    case PropertyDataType.dropdown: {
      return <StringDisplay value={props.property.data as string} />
    }
    case PropertyDataType.file: {
      return props.property.data != null ? (
        <File file={props.property.data as FileType} />
      ) : null
    }
    case PropertyDataType.address: {
      return <div>{(props.property.data as Address).address}</div>
    }
    case PropertyDataType.money: {
      const data = props.property.data as { amount: number; currency: Currency }
      return <Money amount={data.amount} currency={data.currency} />
    }
  }

  return assertUnreachable(props.property.type)
}

type MaybeArray<A> = A | A[]

export const CaseVoucherPropertiesDisplay = (props: {
  value: {
    cases: {
      caseGroupId: string
    }
    caseVoucherProperties: {
      refId: string
      handle: string
      data: PropertyData
      type: PropertyDataType
    }[]
  }
}) => {
  const lang = useSelector((s: RootState) => s.lang)
  const propertyTypes = usePropertyTypes()

  const properties = useMemo(
    () =>
      sortBy(props.value.caseVoucherProperties, (p) => `${p.refId}${p.handle}`),
    [props.value.caseVoucherProperties],
  )

  if (h.isError(propertyTypes)) {
    return <>{lang.misc.loadingError}</>
  }

  if (h.hasData(propertyTypes)) {
    const labelLookup = objectBy(
      filterPropertyTypes(
        propertyTypes.data,
        Entity.CaseVoucher,
        props.value.cases.caseGroupId,
      ),
      (pt) => pt.handle,
    )
    return (
      <>
        {properties.map((value, i) => {
          if (labelLookup[value.handle] == null) {
            return null
          }
          return (
            <div key={`${value.handle}-${value.refId}`}>
              {labelLookup[value.handle]?.label ?? ''}:{' '}
              <PropertyDisplay property={value} />
            </div>
          )
        })}
      </>
    )
  }

  return <Loading width="1em" />
}

export const CasePropertiesDisplay = (props: {
  value: {
    cases: {
      caseGroupId: string
    }
    caseProperties: {
      refId: string
      handle: string
      data: PropertyData
      type: PropertyDataType
    }[]
  }
}) => {
  const lang = useSelector((s: RootState) => s.lang)
  const propertyTypes = usePropertyTypes()

  const properties = useMemo(
    () => sortBy(props.value.caseProperties, (p) => `${p.refId}${p.handle}`),
    [props.value.caseProperties],
  )

  if (h.isError(propertyTypes)) {
    return <>{lang.misc.loadingError}</>
  }

  if (h.hasData(propertyTypes)) {
    const labelLookup = objectBy(
      filterPropertyTypes(
        propertyTypes.data,
        Entity.Case,
        props.value.cases.caseGroupId,
      ),
      (pt) => pt.handle,
    )
    return (
      <>
        {properties.map((value, i) => {
          if (labelLookup[value.handle] == null) {
            return null
          }
          return (
            <div key={`${value.handle}-${value.refId}`}>
              {labelLookup[value.handle]?.label ?? ''}:{' '}
              <PropertyDisplay property={value} />
            </div>
          )
        })}
      </>
    )
  }

  return <Loading width="1em" />
}

export const ContactNameDisplay = (props: {
  value: { contactName: { name: string } | null }
}) => {
  return <StringDisplay value={props.value.contactName?.name ?? ''} />
}

export const ResponsibleDisplay = (props: {
  value: {
    cases: {
      responsibleType: 'team' | 'user' | null
      responsibleReferenceId: string | null
    } | null
  }
}) => {
  if (
    props.value.cases &&
    props.value.cases.responsibleReferenceId != null &&
    props.value.cases.responsibleType != null
  ) {
    return (
      <ResponsiblePreview
        responsibleType={props.value.cases.responsibleType}
        responsibleReferenceId={props.value.cases.responsibleReferenceId}
      />
    )
  }
  return null
}

export const CaseEndReasonDisplay = (props: {
  value: {
    cases: { endReasonId: string | null } | null
    endReasons: {
      label: string
    } | null
  }
}) => {
  const lang = useSelector((s: RootState) => s.lang)

  const endReasonId = props.value.cases?.endReasonId
  const label = props.value.endReasons?.label
  if (endReasonId == null) {
    return null
  }

  const handle = EndReasonHandle[endReasonId]
  return (
    <StringDisplay value={handle ? lang.endReason[handle] : (label ?? '')} />
  )
}

export const CaseStatusDisplay = (props: {
  value: {
    cases: {
      status: CaseStatus
      endReasonId: string | null
      statusTime: string | null
    }
  }
}) => {
  return (
    <CaseStatusComponent
      status={props.value.cases.status}
      endReasonId={props.value.cases.endReasonId}
      statusTime={props.value.cases.statusTime}
    />
  )
}

export const AgentProposalsDisplay = (props: {
  value: {
    agentProposals: { toolName: ToolName; status: AgentToolCallStatus }[] | null
  }
}) => {
  const lang = useSelector((s: RootState) => s.lang)

  if (
    props.value.agentProposals == null ||
    props.value.agentProposals.length === 0
  ) {
    return null
  }

  return (
    <>
      {props.value.agentProposals.map((proposal, i) => (
        <div key={i}>
          {lang.agentToolLabels[proposal.toolName]}{' '}
          <Badge
            theme={
              proposal.status === AgentToolCallStatus.Failed
                ? THEME.DANGEROUS
                : THEME.COLOR_ACCENT
            }
          >
            {proposal.status === AgentToolCallStatus.Failed
              ? lang.agentProposals.failed
              : lang.agentProposals.pending}
          </Badge>
        </div>
      ))}
    </>
  )
}

export const PausedUpdatesDisplay = (props: {
  value: { pausedUpdates: { updateTypeId: string }[] | null }
}) => {
  const lang = useSelector((s: RootState) => s.lang)
  const updateTypesLoader = useUpdateTypes()
  if (!h.hasData(updateTypesLoader)) {
    return <PlaceholderLoader />
  }
  const updateTypes = updateTypesLoader.data
  const updateTypeLookup = objectBy(updateTypes, (ut) => ut.updateTypeId)

  if (props.value.pausedUpdates == null) {
    return ''
  }

  const typeCounts = counted(props.value.pausedUpdates, (ut) => ut.updateTypeId)

  return (
    <>
      {Object.entries(typeCounts).map(([updateTypeId, count]) => {
        return (
          <div>
            <StringDisplay
              value={
                (lang.updateTypes[updateTypeId] ??
                  updateTypeLookup[updateTypeId]?.label ??
                  '') + (count > 1 ? ` (${count})` : '')
              }
            />
          </div>
        )
      })}
    </>
  )
}
